import { ClientPhoneNumber as ClientPhoneRaw } from '@provision/entity-types';
import { gql, Reference, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { QueryVariables, Records } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { removeNotDefinedProperties } from 'src/utils/objects';

export const CLIENT_PHONE_FIELDS = gql`
  fragment ClientPhoneFields on ClientPhoneNumber {
    id
    clientPhoneNumberId
    clientId
    value
    partnerId
    labelId
    smsOptOut
  }
`;

const GET_CLIENT_PHONE_NUMBERS_QUERY = gql`
  query GetClientPhoneNumber($params: QueryParams) @entity(name: "ClientPhoneNumber") {
    getClientPhoneNumber(queryParams: $params) {
      records {
        ...ClientPhoneFields
      }
    }
  }
  ${CLIENT_PHONE_FIELDS}
`;

interface GetClientPhoneQuery {
  getClientPhone: Records<ClientPhone>;
}

export type ClientPhone = Pick<ClientPhoneRaw, 'id' | 'value' | 'partnerId' | 'clientId'>;

export const useClientPhones = (params: { clientId?: string; skip?: boolean }) => {
  const { clientId, skip } = params;

  const { data, ...rest } = useQuery<GetClientPhoneQuery, QueryVariables>(
    GET_CLIENT_PHONE_NUMBERS_QUERY,
    {
      skip: !clientId || skip,
      variables: {
        params: {
          filters: [
            {
              attribute: 'clientId',
              operator: '=',
              value: clientId!,
            },
          ],
        },
      },
    },
  );

  const clientPhones = useMemo(
    () => data?.getClientPhone?.records ?? [],
    [data?.getClientPhone],
  );

  return {
    clientPhones,
    ...rest,
  };
};

const UPDATE_CLIENT_PHONE_NUMBER_MUTATION = gql`
  mutation UpdateClientPhoneNumber(
    $conditions: [ConditionInput]
    $data: ClientPhoneNumberDataInput
  ) {
    updateClientPhoneNumber(conditions: $conditions, data: $data) {
      ...ClientPhoneFields
    }
  }
  ${CLIENT_PHONE_FIELDS}
`;

const CREATE_CLIENT_PHONE_NUMBER_MUTATION = gql`
  mutation CreateClientPhoneNumber($data: ClientPhoneNumberDataInput) {
    createClientPhoneNumber(data: $data) {
      ...ClientPhoneFields
    }
  }
  ${CLIENT_PHONE_FIELDS}
`;

const DELETE_CLIENT_PHONE_NUMBER_MUTATION = gql`
  mutation DeleteClientPhoneNumber($conditions: [ConditionInput]) {
    deleteClientPhoneNumber(conditions: $conditions) {
      ...ClientPhoneFields
    }
  }
  ${CLIENT_PHONE_FIELDS}
`;

export const useClientPhone = () => {
  const { showErrorAlert } = useNotifications();

  const [updateClientPhoneMutation] = useMutation<{
    updateClientPhoneNumber: ClientPhoneRaw[];
  }>(UPDATE_CLIENT_PHONE_NUMBER_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong updating the client phone number, please try again',
      );
    },
  });

  const updateClientPhone = useCallback(
    async (
      clientPhone: Partial<ClientPhoneRaw> & {
        label?: string;
      },
    ) => {
      if (!clientPhone.id) {
        return;
      }

      const patch = removeNotDefinedProperties({
        value: clientPhone.value,
        labelId: clientPhone.labelId || undefined,
      });

      const { data } = await updateClientPhoneMutation({
        variables: {
          conditions: [
            {
              attribute: 'clientPhoneNumberId',
              operator: '=',
              value: clientPhone.id,
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updateClientPhoneNumber: [
            {
              ...clientPhone,
              ...patch,
            } as ClientPhoneRaw,
          ],
        },
      });

      return data?.updateClientPhoneNumber[0];
    },
    [updateClientPhoneMutation],
  );

  const [createClientPhoneMutation] = useMutation<{
    createClientPhoneNumber: ClientPhoneRaw[];
  }>(CREATE_CLIENT_PHONE_NUMBER_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong creating the client phone number, please try again',
      );
    },
  });

  const createClientPhone = useCallback(
    async (
      clientPhone: Partial<ClientPhoneRaw> & {
        label?: string;
      },
    ) => {
      if (!clientPhone.value) {
        return;
      }

      const patch = removeNotDefinedProperties({
        value: clientPhone.value,
        labelId: clientPhone.labelId || undefined,
        partnerId: clientPhone.partnerId,
        clientId: clientPhone.clientId,
      });

      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createClientPhoneMutation({
        variables: {
          data: patch,
        },
        optimisticResponse: {
          createClientPhoneNumber: [
            {
              ...clientPhone,
              ...patch,
              id: clientPhone.id || tempId,
              clientPhoneNumberId: clientPhone.id || tempId,
            } as ClientPhoneRaw,
          ],
        },
        update: (cache, { data: response }) => {
          const created = response?.createClientPhoneNumber?.[0];
          if (!created) return;

          const id = cache.identify({
            __typename: 'Client',
            id: clientPhone.clientId,
          });

          cache.modify({
            id,
            fields: {
              phoneNumbers(existingItems = [], { toReference, readField }) {
                const existingPhones = existingItems as Reference[];
                const id = clientPhone.id || created.id;

                const newPhone = {
                  __typename: 'ClientPhoneNumber',
                  id,
                  value: clientPhone.value || created.value,
                  clientPhoneNumberId: id,
                  labelId: clientPhone.labelId,
                };

                // Quick safety check - if the new phone is already
                // present in the cache, we don't need to add it again.
                if (existingPhones.some((ref) => readField('id', ref) === created.id)) {
                  return existingPhones;
                }

                const reference = toReference(newPhone, true);

                if (reference) {
                  return [reference, ...existingPhones];
                }

                return [newPhone, ...existingPhones];
              },
            },
          });
        },
      });

      return data?.createClientPhoneNumber[0];
    },
    [createClientPhoneMutation],
  );

  const [deleteClientPhoneMutation] = useMutation<{
    deleteClientPhoneNumber: ClientPhoneRaw[];
  }>(DELETE_CLIENT_PHONE_NUMBER_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong deleting the client phone number, please try again',
      );
    },
  });

  const deleteClientPhone = useCallback(
    async (clientPhones?: Partial<ClientPhoneRaw> | Partial<ClientPhoneRaw>[]) => {
      if (
        (Array.isArray(clientPhones) && !clientPhones.length) ||
        (!Array.isArray(clientPhones) && !clientPhones?.id)
      ) {
        return;
      }

      const ids = Array.isArray(clientPhones)
        ? clientPhones.filter((phone) => !!phone.id).map((phone) => phone.id)
        : [clientPhones.id];

      const { data } = await deleteClientPhoneMutation({
        variables: {
          conditions: [
            {
              attribute: 'clientPhoneNumberId',
              operator: 'in',
              value: ids.join(','),
            },
          ],
        },
        optimisticResponse: {
          deleteClientPhoneNumber: Array.isArray(clientPhones)
            ? (clientPhones as ClientPhoneRaw[])
            : [clientPhones as ClientPhoneRaw],
        },
        update: (cache, { data: response }) => {
          const deleted = response?.deleteClientPhoneNumber;
          if (!deleted?.length) return;

          deleted.forEach((phone) => {
            const id = cache.identify({
              __typename: 'Client',
              id: phone.clientId,
            });

            cache.modify({
              id,
              fields: {
                phoneNumbers(existingItems = [], { readField }) {
                  const existingPhones = existingItems as Reference[];
                  return existingPhones.filter(
                    (existingPhone) => readField('id', existingPhone) !== phone.id,
                  );
                },
                primaryPhoneNumber(existingItem = {}, { readField }) {
                  const existingPhone = existingItem as Reference;
                  if (readField('id', existingPhone) === phone.id) {
                    return null;
                  }

                  return existingPhone;
                },
              },
            });
          });
        },
      });

      return data?.deleteClientPhoneNumber;
    },
    [deleteClientPhoneMutation],
  );

  return {
    deleteClientPhone,
    updateClientPhone,
    createClientPhone,
  };
};
